import { useCallback, useEffect, useRef } from 'react';

import { randomUUID } from 'expo-crypto';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { getRewardBenefitId } from 'features/cart/utils';
import {
  buildDiscountOfferCartEntry,
  findRewardBenefitFromHydratedCartEntry,
  repriceMinimalCartEntries as repriceMinimalCartEntriesHelper,
} from 'features/cart/utils/minimal-cart';
import { useAppliedDiscountOffer } from 'features/offer/hooks/use-applied-discount-offer';
import { useEnableLoyaltyServiceRewards } from 'features/reward/hooks/use-enable-loyalty-service-rewards';
import { useServiceRewards } from 'features/reward/state/service-rewards-context';
import { findMatchingRewardForCartEntry, getReward } from 'features/reward/utils/identity';
import { useStoreContext } from 'features/store';
import { omit } from 'lodash-es';

import { ILegacyCartEntry, IOfferDiscount } from '@rbi-ctg/menu';
import { OfferDiscountTypes } from 'enums/menu';
import { useHydrateCartMutation } from 'generated/graphql-gateway';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import {
  validateAppliedReward,
  validateResolvedReward,
} from 'state/global-state/models/loyalty/rewards/rewards.slice';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';
import { LoyaltyOffer } from 'state/loyalty/types';
import { getServiceModeFromLS } from 'state/service-mode';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { getPosServiceMode } from 'state/unified-menu/utils';

import { hydrateEntries } from '../../../hooks/hydrateEntries';
import { useCart } from '../../../hooks/use-cart';

import { useMinimalCartUi } from './use-minimal-cart-ui';

interface IHydrateMinimalCartOptions {
  storeId?: string;
  fetchSanityData?: boolean;
  /**
   * Number of reward application slots freed by an in-progress eviction that has already been
   * dispatched to the legacy cart but whose Redux `appliedLoyaltyRewards` entry has not yet been
   * removed (because the legacy `removeFromCart` does not touch that slice). Adding this value
   * ensures `availableRewardSlots` is computed correctly and `handleRewards` is not skipped for
   * the incoming swap reward.
   */
  rewardSlotsFreedByEviction?: number;
}

type TRewardMinimalCartEntry = ICartEntry & Required<Pick<ICartEntry, 'rewardId' | 'cartId'>>;

/**
 * Expose the cart state and the utility methods to mutate it.
 *
 * When an action to mutate the cart is triggered, it manages the Redux cart actions, logs the events,
 * coordinates with other parts of the code, and dispatches actions on related slices.
 */
export const useMinimalCart = () => {
  const dispatch = useAppDispatch();
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const availableLoyaltyRewardsMap = useAppSelector(
    selectors.loyalty.selectAvailableLoyaltyRewardsMap
  );
  const totalTimesRewardApplied = useAppSelector(selectors.loyalty.selectTotalTimesRewardApplied);
  const stagedCartPoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);
  const { loyaltyUserId } = useLoyaltyUserId();
  const minimalCartPricedStoreNumber = useAppSelector(
    selectors.ordering.selectMinimalCartPricedStoreNumber
  );

  const { store } = useStoreContext();
  const storeId = store?.number;
  const [hydrateCart] = useHydrateCartMutation();
  const { addItemToCart, updateCartEntry } = useCart();
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const rewardRedemptionLimit = useRewardRedemptionLimit();
  const enableMinimalCartUi = useMinimalCartUi();
  const appliedLoyaltyDiscountOffer = useAppliedDiscountOffer();
  const { enabled: serviceRewardsEnabled } = useEnableLoyaltyServiceRewards();
  const { rewardsMap } = useServiceRewards();
  const {
    indexedMenu,
    indexedOffers,
    indexedRewards,
    loadedStoreNumber,
    isLoading: isUnifiedMenuLoading,
  } = useUnifiedMenuContext();

  const handleRewards = useCallback(
    (
      rewardBenefitId: string,
      cartEntry: ILegacyCartEntry,
      remainingPoints: { value: number },
      rewardId?: string
    ) => {
      let cartEntryWithReward = { ...cartEntry };

      const serviceReward = serviceRewardsEnabled
        ? (rewardId ? getReward(rewardsMap, rewardId) : undefined) ??
          findMatchingRewardForCartEntry(Object.values(rewardsMap), cartEntry)
        : undefined;

      const isServiceRewardApplicable = serviceReward
        ? validateResolvedReward(serviceReward, totalTimesRewardApplied, 1, remainingPoints.value)
        : false;

      const { isApplicable, mappedReward } = serviceRewardsEnabled
        ? { isApplicable: false, mappedReward: null }
        : validateAppliedReward(
            availableLoyaltyRewardsMap,
            rewardBenefitId,
            totalTimesRewardApplied,
            1,
            remainingPoints.value
          );

      if (serviceRewardsEnabled && serviceReward && isServiceRewardApplicable) {
        if (!serviceReward.rewardBenefitId) {
          return cartEntryWithReward;
        }
        dispatch(
          actions.loyalty.applyReward({
            cartId: cartEntryWithReward.cartId,
            quantity: 1,
            reward: {
              id: serviceReward.id,
              cmsId: serviceReward.cmsId,
              rewardBenefitId: serviceReward.rewardBenefitId,
              pointCost: serviceReward.pointCost,
              ...(serviceReward.limitPerOrder !== undefined
                ? { limitPerOrder: serviceReward.limitPerOrder }
                : {}),
            },
            redemptionMethod: RewardRedemptionMethod.MOBILE,
            enableRewardUnstacking,
            rewardRedemptionLimit,
          })
        );
        remainingPoints.value -= serviceReward.pointCost;
      } else if (!serviceRewardsEnabled && isApplicable) {
        dispatch(
          actions.loyalty.applyReward({
            rewardBenefitId,
            cartId: cartEntryWithReward.cartId,
            quantity: 1,
            redemptionMethod: RewardRedemptionMethod.MOBILE,
            enableRewardUnstacking,
            rewardRedemptionLimit,
          })
        );
        remainingPoints.value -= mappedReward?.pointCost ?? 0;
      } else {
        const rewardBenefit = findRewardBenefitFromHydratedCartEntry(cartEntry);
        if (rewardBenefit) {
          cartEntryWithReward = { ...rewardBenefit };
          const newId = randomUUID();
          cartEntryWithReward.cartId = newId;
          // @ts-ignore
          cartEntryWithReward.lineId = newId;
        }
      }
      return cartEntryWithReward;
    },
    [
      availableLoyaltyRewardsMap,
      dispatch,
      rewardsMap,
      serviceRewardsEnabled,
      totalTimesRewardApplied,
      enableRewardUnstacking,
      rewardRedemptionLimit,
    ]
  );

  const hydrateMinimalCart = useCallback(
    async (entries?: ICartEntry[], options?: IHydrateMinimalCartOptions) => {
      const serviceMode = getServiceModeFromLS();
      if (!storeId || !serviceMode) {
        return false;
      }

      // When reward unstacking is on, cap how many applyReward dispatches hydrateEntries may
      // fire so that a multi-quantity reward add never evicts an unrelated existing reward.
      // The selector value is stale inside handleRewards, so we compute the available slots
      // once here and pass it as the hard limit.
      //
      // `rewardSlotsFreedByEviction` accounts for a reward that was already removed from the
      // legacy cart (via the legacy `removeFromCart`) but whose Redux `appliedLoyaltyRewards`
      // entry has not been cleaned up yet — so `totalTimesRewardApplied` still counts it. Without
      // this adjustment, `availableRewardSlots` would be 0 during a swap, causing `handleRewards`
      // to be skipped and the incoming reward to land in the cart as a regular item.
      const slotsFreedByEviction = options?.rewardSlotsFreedByEviction ?? 0;
      const availableRewardSlots = enableRewardUnstacking
        ? Math.max(
            0,
            rewardRedemptionLimit -
              Object.values(totalTimesRewardApplied).reduce((sum, v) => sum + v, 0) +
              slotsFreedByEviction
          )
        : undefined;

      return hydrateEntries(
        entries ?? minimalCartEntries,
        options?.storeId || storeId,
        serviceMode,
        hydrateCart,
        addItemToCart,
        updateCartEntry,
        dispatch,
        handleRewards,
        stagedCartPoints,
        loyaltyUserId,
        availableRewardSlots
      );
    },
    [
      addItemToCart,
      dispatch,
      enableRewardUnstacking,
      handleRewards,
      hydrateCart,
      loyaltyUserId,
      minimalCartEntries,
      rewardRedemptionLimit,
      stagedCartPoints,
      storeId,
      totalTimesRewardApplied,
      updateCartEntry,
    ]
  );

  const addDiscountOfferToMinimalCart = useCallback(
    (discountOffer: LoyaltyOffer) => {
      const nonDiscountEntries = minimalCartEntries.filter(
        entry => entry.type !== MinimalCartEntryType.discount
      );
      const discountOfferCartEntry = buildDiscountOfferCartEntry({
        discountOffer,
        cartEntries: nonDiscountEntries,
      });

      if (!discountOfferCartEntry) {
        return;
      }

      if (!enableMinimalCartUi) {
        hydrateMinimalCart([discountOfferCartEntry]);
      }

      dispatch(actions.ordering.addMinimalCartEntry(discountOfferCartEntry));
      dispatch(actions.ordering.setMinimalCartPricedStoreNumber(storeId ?? null));
    },
    [dispatch, enableMinimalCartUi, hydrateMinimalCart, minimalCartEntries, storeId]
  );

  const handleDiscountOfferUpdate = useCallback(() => {
    const discountOffer = minimalCartEntries.find(
      entry => entry.type === MinimalCartEntryType.discount
    );

    if (!appliedLoyaltyDiscountOffer || !discountOffer) {
      return;
    }

    const incentive = appliedLoyaltyDiscountOffer.incentives?.[0] as IOfferDiscount;
    if (discountOffer && incentive.discountType === OfferDiscountTypes.PERCENTAGE) {
      dispatch(actions.ordering.removeMinimalCartEntry(discountOffer));
      addDiscountOfferToMinimalCart(appliedLoyaltyDiscountOffer);
    }
  }, [dispatch, addDiscountOfferToMinimalCart, minimalCartEntries, appliedLoyaltyDiscountOffer]);

  const repriceMinimalCartEntries = useCallback(() => {
    const serviceMode = getServiceModeFromLS();
    if (!enableMinimalCartUi || !serviceMode) {
      return;
    }

    const repricedEntries = repriceMinimalCartEntriesHelper({
      cartEntries: minimalCartEntries,
      lookUpContext: {
        indexedMenu,
        indexedOffers,
        indexedRewards,
      },
      posServiceMode: getPosServiceMode(serviceMode),
      appliedLoyaltyDiscountOffer,
    });

    dispatch(actions.ordering.setMinimalCartEntries(repricedEntries));
    dispatch(actions.ordering.setMinimalCartPricedStoreNumber(storeId ?? null));
  }, [
    appliedLoyaltyDiscountOffer,
    dispatch,
    enableMinimalCartUi,
    indexedMenu,
    indexedOffers,
    indexedRewards,
    minimalCartEntries,
    storeId,
  ]);

  const isMinimalCartStoreRepricing = Boolean(
    enableMinimalCartUi &&
      getServiceModeFromLS() &&
      storeId &&
      minimalCartEntries.length > 0 &&
      (isUnifiedMenuLoading || loadedStoreNumber === storeId) &&
      minimalCartPricedStoreNumber !== storeId
  );

  // A discount offer applied before reload is restored into `appliedOffers`
  // (persisted) immediately, but its full CMS/incentive data
  // (`appliedLoyaltyDiscountOffer`) only arrives later via an async fetch.
  // This one-shot reprice below bakes `appliedLoyaltyDiscountOffer` into the
  // discount minimal-cart-entry's price permanently (it never re-runs for
  // the same store) — firing while that fetch is still in flight would drop
  // or zero-price the discount entry with no way to recover it once the
  // real data arrives. See BKPE-28076 followup.
  const hasUnresolvedDiscountOfferData =
    !appliedLoyaltyDiscountOffer &&
    appliedOffers.some(({ cartId }) => cartId === DISCOUNT_OFFER_CART_ID);

  const shouldRepriceMinimalCartForStore = Boolean(
    enableMinimalCartUi &&
      getServiceModeFromLS() &&
      storeId &&
      minimalCartEntries.length > 0 &&
      loadedStoreNumber === storeId &&
      indexedMenu.isLoaded() &&
      !isUnifiedMenuLoading &&
      !hasUnresolvedDiscountOfferData &&
      minimalCartPricedStoreNumber !== storeId
  );

  // The one-shot reprice above may have already run for this store while
  // `hasUnresolvedDiscountOfferData` was true (e.g. it was gated off before
  // this fix existed, or the async fetch is unusually slow), permanently
  // setting `minimalCartPricedStoreNumber`. Once the discount offer data
  // resolves (or the offer is evicted), force one more reprice so the
  // discount entry's price reflects the now-available data.
  const hadUnresolvedDiscountOfferDataRef = useRef(false);
  useEffect(() => {
    if (hasUnresolvedDiscountOfferData) {
      hadUnresolvedDiscountOfferDataRef.current = true;
      return;
    }
    if (hadUnresolvedDiscountOfferDataRef.current) {
      hadUnresolvedDiscountOfferDataRef.current = false;
      dispatch(actions.ordering.setMinimalCartPricedStoreNumber(null));
    }
  }, [hasUnresolvedDiscountOfferData, dispatch, storeId, minimalCartPricedStoreNumber]);

  const getAppliedReward = useCallback(() => {
    if (enableMinimalCartUi) {
      return minimalCartEntries.find(entry => entry.type === MinimalCartEntryType.reward);
    }
    return minimalCartEntries.find(
      (entry): entry is TRewardMinimalCartEntry => !!entry.rewardId && !!entry.cartId
    );
  }, [enableMinimalCartUi, minimalCartEntries]);

  const getAppliedRewards = useCallback((): TRewardMinimalCartEntry[] => {
    if (enableMinimalCartUi) {
      return minimalCartEntries.filter(
        entry => entry.type === MinimalCartEntryType.reward
      ) as TRewardMinimalCartEntry[];
    }
    return minimalCartEntries.filter(
      (entry): entry is TRewardMinimalCartEntry => !!entry.rewardId && !!entry.cartId
    );
  }, [enableMinimalCartUi, minimalCartEntries]);

  // Adds a single cartEntry to the cart
  const addItemToMinimalCart = useCallback(
    (minimalCartEntry: ICartEntry) => {
      if (enableMinimalCartUi) {
        const appliedReward = getAppliedReward();
        const existingRewardSlotCount = minimalCartEntries.filter(
          e => e.type === MinimalCartEntryType.reward
        ).length;

        const downgradeCondition =
          enableRewardUnstacking &&
          minimalCartEntry.type === MinimalCartEntryType.reward &&
          !!appliedReward &&
          appliedReward.id === minimalCartEntry.id &&
          existingRewardSlotCount >= rewardRedemptionLimit;

        if (downgradeCondition) {
          const benefitCardEntry = minimalCartEntry.children?.[0];
          if (benefitCardEntry.type !== MinimalCartEntryType.combo) {
            dispatch(
              actions.ordering.addMinimalCartEntry({
                ...benefitCardEntry,
                cartId: randomUUID(),
                quantity: 1,
              })
            );
          }
        } else {
          dispatch(actions.ordering.addMinimalCartEntry(minimalCartEntry));
        }
        dispatch(actions.ordering.setMinimalCartPricedStoreNumber(storeId ?? null));

        handleDiscountOfferUpdate();
        return;
      }
      /**
       * When attempting to add several items with applied reward and reward unstacking is enabled,
       * add one entry line for the reward and a separate line for the other items (without the reward)
       */
      if (enableRewardUnstacking && minimalCartEntry.rewardId && minimalCartEntry.quantity > 1) {
        dispatch(actions.ordering.addMinimalCartEntry({ ...minimalCartEntry, quantity: 1 }));
        dispatch(
          actions.ordering.addMinimalCartEntry({
            ...omit(minimalCartEntry, ['rewardId', 'rewardBenefitId', 'cartId']),
            type: MinimalCartEntryType.item,
            cartId: randomUUID(),
            quantity: minimalCartEntry.quantity - 1,
          })
        );
      } else {
        dispatch(actions.ordering.addMinimalCartEntry(minimalCartEntry));
        dispatch(actions.ordering.setMinimalCartPricedStoreNumber(storeId ?? null));
        handleDiscountOfferUpdate();
      }
    },
    [
      enableMinimalCartUi,
      enableRewardUnstacking,
      getAppliedReward,
      handleDiscountOfferUpdate,
      dispatch,
      storeId,
      minimalCartEntries,
      rewardRedemptionLimit,
    ]
  );

  const updateItemInMinimalCart = useCallback(
    (cartId: string, cartEntry: ICartEntry) => {
      dispatch(actions.ordering.updateMinimalCartEntry({ cartId, cartEntry }));
      dispatch(actions.ordering.setMinimalCartPricedStoreNumber(storeId ?? null));
      handleDiscountOfferUpdate();
    },
    [dispatch, handleDiscountOfferUpdate, storeId]
  );

  const emptyMinimalCart = useCallback(() => {
    dispatch(actions.ordering.resetMinimalCart());
    // Mirror `emptyCart`: clear the recovered priced order id so a torn-down minimal cart
    // does not leave a stale (storage-backed, TTL'd) order id that suppresses the cart→menu
    // redirect or gets re-fetched on a hard reload after the order was placed.
    dispatch(actions.ordering.setPricedOrderId(null));
    dispatch(actions.loyalty.setSelectedOffer(null));
    dispatch(actions.loyalty.resetAppliedOffers());
    dispatch(actions.loyalty.resetAppliedDiscountOffers());
    dispatch(
      actions.loyalty.removeAppliedRewards({
        cartIds: minimalCartEntries.map(entry => entry.cartId).filter((id): id is string => !!id),
      })
    );
  }, [dispatch, minimalCartEntries]);

  const removeItemFromMinimalCart = useCallback(
    (cartEntry: ICartEntry) => {
      dispatch(actions.ordering.removeMinimalCartEntry(cartEntry));
      if (
        cartEntry.type !== MinimalCartEntryType.discount &&
        minimalCartEntries.length > 0 &&
        enableMinimalCartUi
      ) {
        handleDiscountOfferUpdate();
      }
      if (cartEntry.type === MinimalCartEntryType.reward) {
        dispatch(
          actions.loyalty.removeAppliedReward({
            rewardBenefitId: getRewardBenefitId(cartEntry) ?? '',
            cartId: cartEntry.cartId ?? '',
          })
        );
      }
    },
    [dispatch, handleDiscountOfferUpdate, minimalCartEntries.length, enableMinimalCartUi]
  );

  const replaceItemInMinimalCart = useCallback(
    (oldCartEntry: ICartEntry, newCartEntry: ICartEntry) => {
      // Use atomic replace action to avoid flicker from two separate dispatches
      dispatch(
        actions.ordering.replaceMinimalCartEntry({
          oldCartId: oldCartEntry.cartId ?? '',
          newEntry: newCartEntry,
        })
      );
      dispatch(actions.ordering.setMinimalCartPricedStoreNumber(storeId ?? null));

      if (enableMinimalCartUi) {
        handleDiscountOfferUpdate();
      }
      if (oldCartEntry.type === MinimalCartEntryType.reward) {
        dispatch(
          actions.loyalty.removeAppliedReward({
            rewardBenefitId: getRewardBenefitId(oldCartEntry) ?? '',
            cartId: oldCartEntry.cartId ?? '',
          })
        );
      }
    },
    [dispatch, handleDiscountOfferUpdate, enableMinimalCartUi, storeId]
  );

  return {
    cartEntries: minimalCartEntries,
    isMinimalCartStoreRepricing,
    shouldRepriceMinimalCartForStore,
    addItemToMinimalCart,
    updateItemInMinimalCart,
    addDiscountOfferToMinimalCart,
    removeItemFromMinimalCart,
    replaceItemInMinimalCart,
    emptyMinimalCart,
    hydrateMinimalCart,
    getAppliedReward,
    getAppliedRewards,
    repriceMinimalCartEntries,
  };
};
